import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewEncapsulation
} from '@angular/core'
import { AbstractControl, UntypedFormGroup } from '@angular/forms'
import { GlobalMessageService, GlobalMessageType } from '@spartacus/core'
import { BehaviorSubject, combineLatest, of, Subscription } from 'rxjs'
import { map } from 'rxjs/operators'
import { EbGenericPageStep } from '../../../../components/generic-page-stepper/eb-generic-page-stepper.interface'
import {
  EbComponentConfig,
  EbFormConfig,
  EbFormDependencyResultMap,
  EbFormGroupConfig
} from '../../interfaces/eb-form.interface'
import { EbFormComponentRendererService } from '../../services/eb-form-component-renderer.service'
import { EbFormDependenciesService } from '../../services/eb-form-dependencies.service'
import { EbFormService } from '../../services/eb-form.service'

@Component({
    selector: 'eb-form',
    templateUrl: './eb-form.component.html',
    styleUrls: ['./eb-form.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class EbFormComponent implements OnInit, OnDestroy {
  constructor(
    protected ebFormService: EbFormService,
    protected ebFormDependenciesService: EbFormDependenciesService,
    protected ebFormComponentRendererService: EbFormComponentRendererService,
    protected globalMessageService: GlobalMessageService,
    protected ebForm: ElementRef
  ) {
    this.toCamelCaseString = ebFormService.toCamelCaseString
    this.toCamelCasePath = ebFormService.toCamelCaseString
    this.toDashedString = ebFormService.toDashedString
  }

  get pageSteps(): EbGenericPageStep[] {
    return this.formConfig.steps?.map(
      (step, index) =>
        ({
          name: `${this.context}.stepper.step.${index}`,
          disabled: false
        }) as EbGenericPageStep
    )
  }
  @Input() formConfig: EbFormConfig
  @Input() context: string
  @Input() showStepper = true
  @Input() submitting = false
  @Input() hideTitle = false
  @Output() formSubmit = new EventEmitter<any>()
  expandedMap: { [path: string]: BehaviorSubject<boolean> } = {}
  form: UntypedFormGroup
  activeStep = 0
  toCamelCasePath: (str: string) => string
  toCamelCaseString: (str: string) => string
  toDashedString: (str: string) => string
  currentStepValid$ = new BehaviorSubject(false)
  dependencyMap: EbFormDependencyResultMap
  subscriptions = new Subscription()

  private static validateControls(controls: AbstractControl[]): boolean {
    return !controls?.find(control => control?.invalid)
  }

  ngOnInit() {
    this.form = this.ebFormService.createFormByConfig(this.formConfig)
    this.dependencyMap = this.ebFormDependenciesService.generateDependencyMap(
      this.formConfig,
      this.form
    )
    this.subscriptions.add(
      this.form.valueChanges.subscribe(() => {
        this.stepValidation()
      })
    )
    this.stepValidation()
  }

  private validateCurrentStep(): boolean {
    const controls = this.getControlsFromStep(this.activeStep)
    return EbFormComponent.validateControls(controls)
  }

  private getControlsFromStep(stepNumber) {
    return this.formConfig.steps[stepNumber].elements
      .filter(element => element.type === 'group' || element.type === 'field')
      .map(element => this.form.get(element.name))
  }

  onFormSubmit() {
    if (this.currentStepValid$.getValue()) {
      this.formSubmit.emit(this.form.value)
    } else if (this.formConfig.stepsAlwaysEnabled) {
      this.showStepValidationErrors()
    }
  }

  onFieldChanging() {
    this.currentStepValid$.next(false)
  }

  onNext() {
    if (this.activeStep < this.formConfig.steps.length - 1) {
      this.onNextStep()
    } else {
      this.onFormSubmit()
    }
  }

  onStepChange(stepNumber) {
    this.activeStep = stepNumber
    this.stepValidation()
  }

  onNextStep() {
    if (this.currentStepValid$.getValue()) {
      this.activeStep++
      this.stepValidation()
    } else if (this.formConfig.stepsAlwaysEnabled) {
      this.showStepValidationErrors()
    }
  }

  onPreviousStep() {
    this.activeStep--
    this.stepValidation()
  }

  private stepValidation() {
    this.currentStepValid$.next(this.validateCurrentStep())
  }

  getState(path, state = 'hide', defaultValue = true) {
    const subjects = this.dependencyMap[path]?.[state]
    if (subjects?.length === 1) {
      return subjects[0]
    } else if (subjects?.length) {
      return combineLatest(subjects).pipe(
        map(bools => {
          return !bools.find(bool => bool === false)
        })
      )
    }
    return of(defaultValue)
  }

  render(config: EbComponentConfig, path: string) {
    this.ebFormComponentRendererService.render(config, path)
    return path
  }

  getExpandedState(path, config: EbFormGroupConfig): BehaviorSubject<boolean> {
    if (this.expandedMap[path] === undefined) {
      this.expandedMap[path] = new BehaviorSubject(!config.collapsed)
    }
    return this.expandedMap[path]
  }

  toggleExpandedState(path) {
    this.expandedMap[path].next(!this.expandedMap[path].getValue())
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe()
  }

  private showStepValidationErrors() {
    this.getControlsFromStep(this.activeStep).forEach(control => control.markAllAsTouched())
    this.globalMessageService.add(
      {
        key: this.toDottedString(this.context) + '.validation.failed'
      },
      GlobalMessageType.MSG_TYPE_WARNING
    )
    requestAnimationFrame(() => {
      this.ebForm.nativeElement.querySelector('[error]')?.focus()
    })
  }

  toDottedString(str: string) {
    return this.toDashedString(str).replace(/-/g, '.')
  }
}
